import { queryOptions } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { FilterInput, OrderInput } from '#features/api/types';
import { defaultLocale } from '#features/i18n/constants/locales';
import { notePushWorthyAction } from '#features/notifications/utils/pushPrompt';
import { envConfig } from '#constants/envConfig';
import { isDefined } from '#utils/isDefined';
import { postFragment } from './fragments';
import { resolveInviteFailure } from './inviteOutcome';

/** PostIds where the current user just triggered a status change (complete/cancel).
 *  Realtime handler checks this to avoid showing a duplicate toast. */
export const suppressedSessionStatusPostIds = new Set<string>();

const suppressSessionStatusToast = (postId: string) => {
  suppressedSessionStatusPostIds.add(postId);
  setTimeout(() => suppressedSessionStatusPostIds.delete(postId), 5000);
};

const getMySessionsDoc = graphql(
  `
    query MySessions($archived: Boolean, $offset: Int, $limit: Int) {
      post {
        mySessions(archived: $archived, offset: $offset, limit: $limit) {
          __typename
          ... on PostList {
            posts {
              ...PostFragment
            }
            hasMore
            totalCount
          }
        }
      }
    }
  `,
  [postFragment]
);

export const getMySessions = (input: { archived: boolean; offset: number; limit: number }) =>
  gqlFetcher(getMySessionsDoc, input).then((data) =>
    data.post.mySessions?.__typename === 'PostList'
      ? {
          records: data.post.mySessions?.posts,
          hasMore: data.post.mySessions?.hasMore,
          totalCount: data.post.mySessions?.totalCount,
        }
      : {
          records: [],
          hasMore: false,
          totalCount: 0,
        }
  );

const getUserSessionsDoc = graphql(
  `
    query UserSessions($userId: UUID!, $archived: Boolean, $offset: Int, $limit: Int) {
      post {
        userSessions(userId: $userId, archived: $archived, offset: $offset, limit: $limit) {
          __typename
          ... on PostList {
            posts {
              ...PostFragment
            }
            hasMore
            totalCount
          }
        }
      }
    }
  `,
  [postFragment]
);

export const getUserSessions = (input: {
  userId: string;
  archived?: boolean;
  offset: number;
  limit: number;
}) =>
  gqlFetcher(getUserSessionsDoc, input).then((data) =>
    data.post.userSessions?.__typename === 'PostList'
      ? {
          records: data.post.userSessions?.posts,
          hasMore: data.post.userSessions?.hasMore,
          totalCount: data.post.userSessions?.totalCount,
        }
      : {
          records: [],
          hasMore: false,
          totalCount: 0,
        }
  );

const getDiscoverSessionsDoc = graphql(
  `
    query DiscoverSessions(
      $offset: Int
      $limit: Int
      $orders: [OrderInput]
      $filters: [FilterInput]
    ) {
      post {
        discoverSessions(offset: $offset, limit: $limit, orders: $orders, filters: $filters) {
          posts {
            ...PostFragment
          }
          hasMore
          totalCount
        }
      }
    }
  `,
  [postFragment]
);

export const getDiscoverSessions = (input: {
  offset: number;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
}) =>
  gqlFetcher(getDiscoverSessionsDoc, input).then((data) => ({
    records: data.post.discoverSessions?.posts?.filter(isDefined),
    hasMore: data.post.discoverSessions?.hasMore,
    totalCount: data.post.discoverSessions?.totalCount,
  }));

const getSessionHistoryDoc = graphql(`
  query SessionHistory($postId: UUID!, $offset: Int, $limit: Int) {
    post {
      sessionHistory(postId: $postId, offset: $offset, limit: $limit) {
        hasMore
        totalCount
        sessions {
          id
          postId
          createdAt
          updatedAt
          startedAt
          expiredAt
          teamLimit
          status
          discordVoice
          voiceMode
          voiceRoom {
            connectedCount
            maxParticipants
            status
          }
          acceptedMembersCount
        }
      }
    }
  }
`);

const getPostSessionsDoc = graphql(`
  query PostSessions($postId: UUID!, $offset: Int, $limit: Int) {
    post {
      sessions(postId: $postId, offset: $offset, limit: $limit) {
        hasMore
        totalCount
        sessions {
          id
          postId
          createdAt
          updatedAt
          startedAt
          expiredAt
          teamLimit
          status
          discordVoice
          voiceMode
          voiceRoom {
            connectedCount
            maxParticipants
            status
          }
          acceptedMembersCount
          sessionChat {
            id
            unreadCount
          }
          members {
            memberId
            status
            userData {
              avatar
              displayName
              socials {
                id
                displayName
                type
                visible
              }
            }
          }
        }
      }
    }
  }
`);

export const getPostSessions = (input: { postId: string; offset: number; limit: number }) =>
  gqlFetcher(getPostSessionsDoc, input).then((data) => ({
    records: (data.post.sessions?.sessions ?? []).filter(isDefined),
    hasMore: data.post.sessions?.hasMore ?? false,
    totalCount: data.post.sessions?.totalCount ?? 0,
  }));

/**
 * Есть ли у сессии переписка — одним полем, без состава и голоса.
 *
 * Отдельный запрос, а не общий `PostSessions`: тот тянет участников, голосовую комнату и профиль
 * каждого, а здесь нужен один идентификатор, и спрашиваем мы его несколько раз подряд, пока чат
 * не появится (его заводит другой сервис по событию).
 *
 * Список сервер отдаёт по убыванию `startedAt` (PostSessionRepository.findAllByPostIdOrderByStartedAtDesc),
 * поэтому только что созданная сессия всегда в начале — пятидесяти строк заведомо хватает.
 */
const getSessionChatIdDoc = graphql(`
  query SessionChatId($postId: UUID!) {
    post {
      sessions(postId: $postId, offset: 0, limit: 50) {
        sessions {
          id
          sessionChat {
            id
          }
        }
      }
    }
  }
`);

export const getSessionChatId = (input: { postId: string; sessionId: string }) =>
  gqlFetcher(getSessionChatIdDoc, { postId: input.postId }).then(
    (data) =>
      (data.post.sessions?.sessions ?? []).find((session) => session?.id === input.sessionId)
        ?.sessionChat?.id ?? null
  );

export const getSessionHistory = (input: { postId: string; offset: number; limit: number }) =>
  gqlFetcher(getSessionHistoryDoc, input).then((data) => ({
    records: (data.post.sessionHistory?.sessions ?? []).filter(isDefined),
    hasMore: data.post.sessionHistory?.hasMore ?? false,
    totalCount: data.post.sessionHistory?.totalCount ?? 0,
  }));

/** Где команда будет говорить. Значения совпадают с серверным enum. */
export const VOICE_MODE = {
  platform: 'PLATFORM',
  discord: 'DISCORD',
  inGame: 'IN_GAME',
  none: 'NONE',
} as const;

export type VoiceMode = (typeof VOICE_MODE)[keyof typeof VOICE_MODE];

const createSessionDoc = graphql(
  `
    mutation CreateSession(
      $postId: UUID!
      $startedAt: Instant
      $expiredAt: Instant
      $durationMinutes: Int
      $teamLimit: Int!
      $discordVoice: Boolean!
      $voiceMode: String
      $playNow: Boolean
    ) {
      session {
        create(
          input: {
            postId: $postId
            startedAt: $startedAt
            expiredAt: $expiredAt
            durationMinutes: $durationMinutes
            teamLimit: $teamLimit
            discordVoice: $discordVoice
            voiceMode: $voiceMode
            playNow: $playNow
          }
        ) {
          __typename
          ... on Post {
            ...PostFragment
            # id именно созданного сбора: activeSession у заявки с идущей игрой указывает
            # на сегодняшний сбор, и приглашение увело бы друга не туда
            createdSessionId
          }
          ... on GlobalError {
            errorCode
            errorMessage
          }
          ... on InputError {
            errors {
              messagesErrors
              name
            }
          }
        }
      }
    }
  `,
  [postFragment]
);

export const createSession = (input: {
  postId: string;
  startedAt?: string;
  expiredAt?: string;
  durationMinutes?: number;
  teamLimit: number;
  /** @deprecated режим описывает voiceMode; флаг шлём синхронно, пока его читают старые сборки. */
  discordVoice: boolean;
  /** PLATFORM | DISCORD | NONE. */
  voiceMode?: VoiceMode;
  playNow?: boolean;
}) => gqlFetcher(createSessionDoc, input).then((data) => data.session.create);

const joinSessionDoc = graphql(`
  mutation JoinSession($sessionId: UUID!) {
    session {
      join(input: { sessionId: $sessionId }) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

/**
 * Сколько ждать ответ на «Вступить».
 *
 * Мутация сама срока не имеет, и на мёртвой сети кнопка крутилась бы до тайм-аута браузера, а
 * человек не знал бы, вступил он или нет. По истечении срока приходит обычная ошибка сети, тост
 * говорит об этом, а экран перечитывает состав: запрос мог дойти, и правду знает только сервер.
 */
export const JOIN_SESSION_TIMEOUT_MS = 30_000;

export const joinSession = (input: { sessionId: string }) =>
  gqlFetcher(joinSessionDoc, input, { timeoutMs: JOIN_SESSION_TIMEOUT_MS }).then((data) => {
    // Вошёл в чужую сессию — дальше всё общение идёт мимо экрана, и пуши нужны. Отказ (нет мест,
    // пересечение по времени) приезжает полем ответа и тоже резолвится: человека не пустили,
    // вопрос про пуши он не ждёт, и всплывать ему поверх блока с отказом нельзя.
    if (!resolveInviteFailure(data.session.join)) {
      notePushWorthyAction();
    }
    return data.session.join;
  });

const clashingSessionDoc = graphql(`
  query ClashingSession($sessionId: UUID!) {
    session {
      clashingSession(sessionId: $sessionId) {
        sessionId
        postId
        startedAt
        teamLimit
        acceptedMembersCount
        kind
        pendingInvites
      }
    }
  }
`);

/**
 * Чем занято это время: моя сессия, которая пересекается с указанной.
 *
 * Считает сервер, и это не лень клиента. Отказ несёт адрес мешающей ЗАЯВКИ, а у заявки бывает
 * несколько сессий: за 90 дней такое было в 14% отказов. Экран брал с заявки самую раннюю живую
 * сессию и в этих случаях показывал чужое время, а кнопку мог спрятать там, где переход прошёл бы.
 */
export const getClashingSession = (input: { sessionId: string }) =>
  gqlFetcher(clashingSessionDoc, input).then((data) => data.session.clashingSession);

export const clashingSessionQueryOptions = (sessionId: string | null) =>
  queryOptions({
    queryKey: ['clashing-session', sessionId],
    queryFn: () => getClashingSession({ sessionId: sessionId! }),
    enabled: Boolean(sessionId),
  });

const switchSessionDoc = graphql(`
  mutation SwitchSession($sessionId: UUID!) {
    session {
      switchSession(input: { sessionId: $sessionId }) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

/**
 * Освободить свою сессию на пересекающееся время и войти в эту, одной операцией.
 *
 * Единственное деструктивное действие над сессиями: своя пустая закрывается, из чужой человек
 * выходит. Сервер откажет, если в покидаемой сессии успели появиться люди, поэтому предлагать
 * переход можно только там, где бросать некого.
 */
export const switchSession = (input: { sessionId: string }) =>
  gqlFetcher(switchSessionDoc, input).then((data) => {
    if (!resolveInviteFailure(data.session.switchSession)) {
      notePushWorthyAction();
    }
    return data.session.switchSession;
  });

const leaveSessionDoc = graphql(`
  mutation LeaveSession($sessionId: UUID!) {
    session {
      leave(input: { sessionId: $sessionId }) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const leaveSession = (input: { sessionId: string }) =>
  gqlFetcher(leaveSessionDoc, input).then((data) => data.session.leave);

const rejectSessionMemberDoc = graphql(`
  mutation RejectSessionMember($userId: UUID!, $sessionId: UUID!) {
    session {
      reject(input: { memberId: $userId, sessionId: $sessionId }) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const rejectSessionMember = (input: { userId: string; sessionId: string }) =>
  gqlFetcher(rejectSessionMemberDoc, input).then((data) => data.session.reject);

const cancelSessionDoc = graphql(`
  mutation CancelSession($sessionId: UUID!) {
    session {
      cancel(input: { sessionId: $sessionId }) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const cancelSession = (input: { sessionId: string; postId: string }) => {
  suppressSessionStatusToast(input.postId);
  return gqlFetcher(cancelSessionDoc, { sessionId: input.sessionId }).then(
    (data) => data.session.cancel
  );
};

const hideSessionDoc = graphql(`
  mutation HideSession($sessionId: UUID!) {
    session {
      hide(input: { sessionId: $sessionId }) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

/**
 * Убрать прошедшую сессию из архива заявки.
 *
 * Скрытие, а не удаление: на сессии висят участники, журнал приглашений и опрос после игры, по
 * которым считаются активация и конверсия приглашений. Снести строку значило бы проделать дыру
 * в метриках ради косметики, поэтому сервер только ставит отметку времени.
 */
export const hideSession = (input: { sessionId: string }) =>
  gqlFetcher(hideSessionDoc, { sessionId: input.sessionId }).then((data) => data.session.hide);

const completeSessionDoc = graphql(`
  mutation CompleteSession($sessionId: UUID!) {
    session {
      complete(input: { sessionId: $sessionId }) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const completeSession = (input: { sessionId: string; postId: string }) => {
  suppressSessionStatusToast(input.postId);
  return gqlFetcher(completeSessionDoc, { sessionId: input.sessionId }).then(
    (data) => data.session.complete
  );
};

const inviteToSessionDoc = graphql(`
  mutation InviteToSession($sessionId: UUID!, $memberId: UUID!, $audienceSource: String) {
    session {
      invite(
        input: { sessionId: $sessionId, memberId: $memberId, audienceSource: $audienceSource }
      ) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

/**
 * Из какой витрины позвали – для журнала участников сессии. Метка аналитическая: сервер её не
 * проверяет и не использует для решений (подделывается с клиента). Без неё источник неизвестен.
 */
export type InviteAudienceSource = 'FRIEND' | 'RECENT_TEAMMATE' | 'RECOMMENDATION' | 'OTHER';

export const inviteToSession = (input: {
  sessionId: string;
  memberId: string;
  audienceSource?: InviteAudienceSource;
}) =>
  gqlFetcher(inviteToSessionDoc, {
    sessionId: input.sessionId,
    memberId: input.memberId,
    audienceSource: input.audienceSource ?? null,
  }).then((data) => data.session.invite);

const acceptSessionInviteDoc = graphql(`
  mutation AcceptSessionInvite($sessionId: UUID!) {
    session {
      acceptInvite(input: { sessionId: $sessionId }) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const acceptSessionInvite = (input: { sessionId: string }) =>
  gqlFetcher(acceptSessionInviteDoc, input).then((data) => data.session.acceptInvite);

const declineSessionInviteDoc = graphql(`
  mutation DeclineSessionInvite($sessionId: UUID!) {
    session {
      declineInvite(input: { sessionId: $sessionId }) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const declineSessionInvite = (input: { sessionId: string }) =>
  gqlFetcher(declineSessionInviteDoc, input).then((data) => data.session.declineInvite);

// --- Приглашение по ссылке ------------------------------------------------------------------

const createInviteLinkDoc = graphql(`
  mutation CreateInviteLink($sessionId: UUID!) {
    session {
      createInviteLink(input: { sessionId: $sessionId }) {
        __typename
        ... on SessionInviteLink {
          code
          postId
          sessionId
          expiresAt
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const createInviteLink = (input: { sessionId: string }) =>
  gqlFetcher(createInviteLinkDoc, input).then((data) => data.session.createInviteLink);

const resolveInviteLinkDoc = graphql(`
  query ResolveInviteLink($code: String!, $trackClick: Boolean) {
    session {
      resolveInviteLink(code: $code, trackClick: $trackClick) {
        code
        sessionId
        postId
        gameId
        inviterUserId
        inviterDisplayName
        inviterAvatar
        startedAt
        expiredAt
        teamLimit
        acceptedCount
        freeSeats
        status
      }
    }
  }
`);

/**
 * Публичный путь: работает и без входа — по нему рисуется лендинг приглашения.
 *
 * `trackClick` отделяет отрисовку страницы от перехода человека. Серверный запрос не несёт куку
 * авторизации (на сервере её неоткуда взять), поэтому засчитанный там переход навсегда остался бы
 * безымянным — а имя и отвечает на вопрос «кто зарегистрировался по ссылке». Считает переход
 * браузер, уже с кукой.
 */
export const resolveInviteLink = (input: { code: string; trackClick?: boolean }) =>
  gqlFetcher(resolveInviteLinkDoc, {
    code: input.code,
    trackClick: input.trackClick ?? false,
  }).then((data) => data.session.resolveInviteLink);

const redeemInviteLinkDoc = graphql(`
  mutation RedeemInviteLink($code: String!) {
    session {
      redeemInviteLink(code: $code)
    }
  }
`);

const switchByInviteLinkDoc = graphql(`
  mutation SwitchByInviteLink($code: String!) {
    session {
      switchByInviteLink(code: $code)
    }
  }
`);

/**
 * Освободить своё место на это время и войти по ссылке.
 *
 * Своя операция, а не обычный переход: у ссылочного входа свои правила, в том числе поблажка по
 * привязанному Discord - по ссылке приходит человек с улицы, часто регистрирующийся ради неё
 * прямо сейчас, и требовать с него привязку на последнем шаге значит потерять ровно того, ради
 * кого ссылка сделана.
 */
export const switchByInviteLink = (input: { code: string }) =>
  gqlFetcher(switchByInviteLinkDoc, input).then((data) => data.session.switchByInviteLink);

/**
 * Вход в состав по ссылке. Возвращает исход строкой — см. INVITE_REDEEM_STATUS: отказ здесь
 * это экран с продолжением, а не ошибка.
 */
export const redeemInviteLink = (input: { code: string }) =>
  gqlFetcher(redeemInviteLinkDoc, input).then((data) => data.session.redeemInviteLink);

const markInviteLinkCopiedDoc = graphql(`
  mutation MarkInviteLinkCopied($code: String!) {
    session {
      markInviteLinkCopied(code: $code) {
        __typename
      }
    }
  }
`);

/** Отметка «приглашение отправлено» — первое из двух чисел воронки. */
export const markInviteLinkCopied = (input: { code: string }) =>
  gqlFetcher(markInviteLinkCopiedDoc, input).then((data) => data.session.markInviteLinkCopied);

export const INVITE_REDEEM_STATUS = {
  joined: 'JOINED',
  alreadyMember: 'ALREADY_MEMBER',
  teamFull: 'TEAM_FULL',
  overlap: 'OVERLAP',
  /**
   * Освободить место нельзя: в своей сессии у человека уже есть игроки. Отдельный исход, а не
   * `overlap`: там предлагают перейти одним нажатием, здесь предлагать нечего.
   */
  overlapHasMembers: 'OVERLAP_HAS_MEMBERS',
  linkInactive: 'LINK_INACTIVE',
  sessionEnded: 'SESSION_ENDED',
  memberRejected: 'MEMBER_REJECTED',
} as const;

export type InviteRedeemStatus = (typeof INVITE_REDEEM_STATUS)[keyof typeof INVITE_REDEEM_STATUS];

/**
 * Приглашение не удалось проверить — сервис отказал. Свой статус, а не `LINK_INACTIVE`: «ссылка
 * устарела» человек читает как приговор и уходит, хотя ссылка жива и достаточно обновить страницу.
 * Значение служебное, с сервера не приходит.
 */
export const INVITE_RESOLVE_FAILED = 'RESOLVE_FAILED';

/**
 * Адрес приглашения. Собирает фронт: шлюз своего хоста не знает, а мы знаем.
 *
 * Локаль в адресе — не украшение: без префикса русскоязычный получатель попадает на английский
 * `/posts/...`, роутер редиректит его на `/ru/...` документной перезагрузкой (`reloadDocument`),
 * и в приложении это перезапуск WebView, а в вебе — лишний переход, который вдобавок считается
 * в воронке дважды. Английская локаль префикса не имеет и подставляется пустой строкой.
 *
 * `session` — тот же параметр, что ставит в письмах `ActorEnrichmentService`: он раскрывает
 * нужный сбор на странице заявки. Без него приглашённый попадал на заявку целиком и искал
 * строку, в которую его зовут, глазами.
 */
export const buildInviteUrl = (
  postId: string,
  code: string,
  locale?: string,
  sessionId?: string | null
) => {
  const prefix = locale && locale !== defaultLocale ? `/${locale}` : '';
  const session = sessionId ? `&session=${sessionId}` : '';
  return `${envConfig.HOST ?? ''}${prefix}/posts/${postId}?invite=${code}${session}`;
};

const increaseTeamLimitDoc = graphql(`
  mutation IncreaseTeamLimit($sessionId: UUID!, $teamLimit: Int!) {
    session {
      increaseTeamLimit(sessionId: $sessionId, teamLimit: $teamLimit) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

/**
 * Добавить место в уже созданном сборе. Только вверх: уменьшить размер команды нельзя — люди
 * уже в составе.
 */
export const increaseTeamLimit = (input: { sessionId: string; teamLimit: number }) =>
  gqlFetcher(increaseTeamLimitDoc, input).then((data) => data.session.increaseTeamLimit);

const enableTeamplayVoiceDoc = graphql(`
  mutation EnableTeamplayVoice($sessionId: UUID!) {
    session {
      enableTeamplayVoice(sessionId: $sessionId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

/** Enable voice without changing the session, roster or chat. The server checks ownership. */
export const enableTeamplayVoice = (input: { sessionId: string }) =>
  gqlFetcher(enableTeamplayVoiceDoc, input).then((data) => data.session.enableTeamplayVoice);

const extendSessionDoc = graphql(`
  mutation ExtendSession($sessionId: UUID!) {
    session {
      extendSession(sessionId: $sessionId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

/**
 * Продлить сессию на 30 минут. Шаг и потолок (8 часов от старта) держит сервер: клиент только
 * просит, поэтому нового времени в запросе нет.
 */
export const extendSession = (input: { sessionId: string }) =>
  gqlFetcher(extendSessionDoc, input).then((data) => data.session.extendSession);
